import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";


import { CONSULAR_FAQ_CATEGORIES } from "@/content/consular-corpus/faq-defs";
import { CONSULAR_DOC_LANDINGS } from "@/content/consular-corpus/documents";
import { CONSULAR_COUNTRY_NAMES as CONSULAR_COUNTRY_LANDINGS } from "@/content/consular-corpus/names.gen";

export const Route = createFileRoute("/en/consular-legalization/faq/")({
  loader: async (): Promise<{ CONSULAR_FAQ_EN: { q: string; a: string }[]; CONSULAR_KEYWORDS_EN: string[]; CONSULAR_QUESTIONS_EN: string[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/consular-corpus/faq"),
      import("@/content/consular-corpus"),
    ]);
    return { CONSULAR_FAQ_EN: m0.CONSULAR_FAQ_EN, CONSULAR_KEYWORDS_EN: m1.CONSULAR_KEYWORDS_EN, CONSULAR_QUESTIONS_EN: m1.CONSULAR_QUESTIONS_EN };
  },
  component: ConsularFaqHubEn,
  head: ({ loaderData }) => {
    const { CONSULAR_FAQ_EN = [], CONSULAR_KEYWORDS_EN = [], CONSULAR_QUESTIONS_EN = [] } = loaderData ?? {};

    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: "Thai MFA Legalization FAQ 2026 — Fees, Timeline & Apostille",
        description:
          "Expat guide to Thai consular legalization at the Department of Consular Affairs, Chaeng Watthana — THB 200/400 fees, 1–3 day turnaround, POA rules, certified translation and the 2026 Apostille switch.",
        path: "/en/consular-legalization/faq",
        type: "article",
        alternates: [{ lang: "th", path: "/consular-legalization/faq" }],
        keywords: CONSULAR_KEYWORDS_EN.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "MFA Legalization FAQ", url: `${site}/en/consular-legalization/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(CONSULAR_FAQ_EN)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            inLanguage: "en",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
            keywords: CONSULAR_KEYWORDS_EN.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/consular-legalization/faq")) },
      ],
    };
  },
});

function ConsularFaqHubEn() {
  const { CONSULAR_FAQ_EN, CONSULAR_KEYWORDS_EN, CONSULAR_QUESTIONS_EN } = Route.useLoaderData() as { CONSULAR_FAQ_EN: { q: string; a: string }[]; CONSULAR_KEYWORDS_EN: string[]; CONSULAR_QUESTIONS_EN: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>MFA Legalization FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          Thai Consular Legalization FAQ — {CONSULAR_FAQ_EN.length} Answers (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          Attorney-written answers on legalizing documents at the Legalization Division, Department of
          Consular Affairs (Chaeng Watthana, Bangkok) — official fees, express same-day service, power of
          attorney rules, certified translation standards, destination-embassy chains, and Thailand's 2026
          Apostille accession.
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/consular-legalization/faq" className="text-primary underline">
            อ่านฉบับภาษาไทย →
          </a>
        </div>

        <section aria-label="Consular legalization FAQ categories" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Browse by topic</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {CONSULAR_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/en/consular-legalization/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleEn} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Legalisation by document type" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Legalisation by document type</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_DOC_LANDINGS.map((d) => (
              <a key={d.slug} href={`/en/consular-legalization/document/${d.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {d.titleEn}
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Legalisation by destination country" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">Legalisation by destination country</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_COUNTRY_LANDINGS.map((c) => (
              <a key={c.slug} href={`/en/consular-legalization/country/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.nameEn}
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {CONSULAR_FAQ_EN.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Let us run the legalization for you</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Door-to-door pickup → certified translation → MFA legalization → destination embassy or Apostille →
            worldwide DHL with tracking.
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            Talk to our team →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">People also ask ({CONSULAR_QUESTIONS_EN.length} questions)</h2>
          <ul className="grid gap-1 text-sm text-muted-foreground md:grid-cols-2">
            {CONSULAR_QUESTIONS_EN.slice(0, 120).map((q, i) => (
              <li key={i}>• {q}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">Search topics ({CONSULAR_KEYWORDS_EN.length} keywords)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_KEYWORDS_EN.slice(0, 300).map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>
      
        <FaqSourceNote topic="consular" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}